// Angular
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
// Store
import { Store } from '@ngrx/store';
// Misc
import { findKey as _findKey, get as _get, has as _has, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
  DriverDefaults,
  FnolModel,
  fnolQuery,
  fromFnolActions
} from '@amfam/claim/dynamic-fnol/data-access';
import {
  DraftClaim,
  fromClaimFnolActions,
  StorePolicyInfo,
  StorePolicyInfoClass
} from '@amfam/claim/fnol/data-access';
// Services
import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
// Models
import { FnolRiskModel, GenericProductType, RiskCompare } from '@amfam/policy/models';
// rxjs
import { AnalyticsFacade, ClaimsAnalytics, FirstPartyFnolAnalytics } from '@amfam/shared/analytics';
// Analytics
import { ClaimsNavigationPaths, CustomerPreFnolModel, Party } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService
} from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-report-claim-risk-selection',
  templateUrl: './report-claim-risk-selection.component.html',
  styleUrls: ['./report-claim-risk-selection.component.scss'],
  standalone: false
})
export class ReportClaimRiskSelectionComponent implements OnInit, OnDestroy {
  public riskSelectionForm: UntypedFormGroup;
  public dataLoading: Observable<boolean>;
  public risks: Array<FnolRiskModel> = new Array<FnolRiskModel>();
  currentRisk: FnolRiskModel;
  public preSelectedRisk: FnolRiskModel;
  private draftClaim: DraftClaim | FnolModel;
  public otherText = '';
  claimsEnterpriseApp = false;

  amfamDotCom: string = env.links.amfamDotComUrl;
  contactNumber: string = env.numberLink;
  private firstName: string;
  private lastName: string;
  private partyId: string;
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    public spinner: LoadingSpinnerService,
    private store: Store,
    private analyticsFacade: AnalyticsFacade,
    private formBuilder: UntypedFormBuilder,
    public roleService: ImpersonateRolesService,
    private copyService: CopyService,
    private feature: FeatureFlagService,
    private modalService: DsModalService,
    private appService: ApplicationService
  ) {
    this.spinner.start({
      blockActions: true
    });
  }

  ngOnInit() {
    this.claimsEnterpriseApp = this.appService.isApp(Applications.FIRST_PARTY_FNOL)
      ? false
      : this.feature.isEnabled('claims_enterprise_app');
    const fnolCopyService: any = this.copyService.getComponentData('claims').fnol;
    this.otherText = fnolCopyService.riskNotFoundContact;

    // Async Pipe aggregate loading information
    this.dataLoading = this.store.select(PolicySelectors.selectPoliciesLoading);
    this.buildForm();

    combineLatest([
      this.store.select(PolicySelectors.selectClaimableRiskModels),
      this.store.select(fnolQuery.getCurrentFnol),
      this.store.select(userQuery.selectFirstName),
      this.store.select(userQuery.selectLastName),
      this.store.select(userQuery.selectCustomerId),
      this.store.select(BrandSelectors.selectPrettyClaimPhoneNum)
    ])
      .pipe(takeUntil(this.stop$))
      .subscribe(([risks, draftClaim, firstName, lastName, partyId, phoneNumber]) => {
        this.risks = risks;

        this.otherText =
          fnolCopyService.riskNotFoundContact1 +
          (phoneNumber
            ? '<a href="tel:' + phoneNumber + '">' + phoneNumber + '</a>'
            : '<a href="' + env.fnolContactNumberHref + '">' + env.fnolContactNumber + '</a>') +
          fnolCopyService.riskNotFoundContact2;

        // Vehicle not on Policy - Only run if Converse Auto is selected and user has an Auto risk
        if (
          this.feature.isEnabled('auto_fnol') &&
          this.risks.some(risk => risk.generalizedProductType === GenericProductType.Auto)
        ) {
          // We need to create a risk using an arbitrary auto risk that the user has - any auto
          // policy they have will work
          const vehicleNotOnPolicy = Object.assign(
            {},
            this.risks.find(risk => risk.generalizedProductType === GenericProductType.Auto),
            {
              id: '-1',
              vehicleTypeName: DriverDefaults.VEHICLE_NOT_ON_POLICY
            }
          ) as FnolRiskModel;

          if (!this.risks.find(risk => risk.id === vehicleNotOnPolicy.id)) {
            this.risks.push(vehicleNotOnPolicy);
          }
        }

        this.risks = this.risks.sort(RiskCompare.compare);
        if (this.risks.length === 1) {
          this.currentRisk = this.risks[0];
        }
        this.draftClaim = draftClaim;
        this.firstName = firstName;
        this.lastName = lastName;
        this.partyId = partyId;
        // Pre-select risk when loaded from edit summary.
        if (this.draftClaim && !!_get(this.draftClaim, 'risk.id', '')) {
          if (_some(this.risks, { id: this.draftClaim.risk.id })) {
            this.preSelectedRisk = this.risks.find(risk => risk.id === this.draftClaim.risk.id);
          }
        }
        this.spinner.stop();
      });
    if (this.appService.isApp(Applications.FIRST_PARTY_FNOL)) {
      this.analyticsFacade.trackPage(FirstPartyFnolAnalytics.GuestRiskSelection);
    } else {
      this.analyticsFacade.trackPage(ClaimsAnalytics.pageReportClaimRiskSelection);
    }
  }

  buildForm() {
    this.riskSelectionForm = this.formBuilder.group({});
  }

  changeRisk(riskId) {
    this.currentRisk = this.risks.find(risk => risk.id === riskId);
  }

  cancel() {
    if (this.appService.isApp(Applications.FIRST_PARTY_FNOL)) {
      this.modalService.open('firstPartyFnolCancelModal');
    } else {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [ClaimsNavigationPaths.CLAIM_HOME_PATH]
        })
      );
    }
  }

  riskSelectionSubmit() {
    if (
      this.feature.isEnabled('auto_fnol') &&
      this.currentRisk &&
      this.currentRisk.generalizedProductType === GenericProductType.Auto
    ) {
      this.routeToDynamicFlow();
    } else {
      /**
       * AS: This would just update the store with this payload information as we do not have all the information yet to
       * make the create draft claim request yet.
       */

      /**
       * AS: In case we have a vin number i.e if the user navigated to the summary page and is coming back using browser back
       * we need to check if the selected risk is same as the one in the store. In case it is dont do anything.
       */
      if (!!this.currentRisk) {
        const vin = _get(this.currentRisk, 'vin', '');
        if (
          !_findKey(
            this.draftClaim,
            claim => _get(claim, 'autoClaimDetail.vehicles.vin', '') === vin
          )
        ) {
          let riskSelectionPayload: StorePolicyInfo;
          if (this.currentRisk) {
            riskSelectionPayload = StorePolicyInfoClass.create(
              this.currentRisk.policyNumber,
              this.currentRisk.id,
              this.currentRisk.year,
              this.currentRisk.make,
              this.currentRisk.model,
              this.currentRisk.vin,
              this.firstName,
              this.lastName,
              this.partyId,
              this.currentRisk.address
            );
          }
          this.store.dispatch(new fromClaimFnolActions.SaveDraftClaim(riskSelectionPayload));
        }
      }

      if (!this.currentRisk) {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [ClaimsNavigationPaths.CLAIM_CALLUS_CONTACT_PATH]
          })
        );
      } else if (_has(this.currentRisk, 'vehicleTypeName')) {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [ClaimsNavigationPaths.CLAIM_INCIDENT_TIME_PATH]
          })
        );
      } else {
        // Route to dynamic property fnol
        if (this.feature.isEnabled('property_fnol')) {
          this.routeToDynamicFlow();
        } else {
          // Route to static page
          this.store.dispatch(
            fromRouterActions.Go({
              path: [ClaimsNavigationPaths.REPORT_CLAIM]
            })
          );
        }
      }
    }
  }

  private routeToDynamicFlow(): void {
    this.store
      .select(userQuery.selectPartyDetails)
      .pipe(take(1))
      .subscribe(party => {
        const customerPreFnolInfo: CustomerPreFnolModel = this.buildCustomerPreFnolInfo(party);
        this.store.dispatch(new fromFnolActions.SaveInfoForDraftClaim(customerPreFnolInfo));
        if (
          customerPreFnolInfo.risk.generalizedProductType === GenericProductType.Auto &&
          this.feature.isEnabled('auto_fnol')
        ) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [ClaimsNavigationPaths.DYNAMIC_FNOL_DRIVER_SELECTION]
            })
          );
        } else {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [ClaimsNavigationPaths.CLAIM_CREATE_INCIDENT_INFO]
            })
          );
        }
      });
  }

  private buildCustomerPreFnolInfo(party: Party): CustomerPreFnolModel {
    return {
      cdhId: this.partyId,
      firstName: this.firstName,
      lastName: this.lastName,
      risk: this.currentRisk,
      emailAddress: _get(party, 'primaryEmail.emailAddress', ''),
      phoneNumber:
        _get(party, 'homePhone.tenDigitPhoneNumber', '') ||
        _get(party, 'primaryPhone.tenDigitPhoneNumber', '')
    };
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
